// Shared money projection (polish.P7): the Budget (M5 target) → actual EBITDA
// bridge that the GM landing's bridge card and Cost & revenue's P&L tab both
// draw. Moved verbatim from `modules/overview/EbitdaBridge.transform.ts`; the
// default period is the GM landing's closed month, so its output is unchanged.
import { gmMoney } from '@/versions/v1/modules/overview/GmMoney.transform';
import type { CostPeriod } from '@/versions/v1/modules/cost/Scene.transform';
export type EbitdaBridgeRow = { id:string; label:string; value:number; bold?:boolean; barPct:number };
export function ebitdaBridgeActual(period: CostPeriod = 'month'): number { return gmMoney(period).mtd.ebitda.actual/1000; }
export function ebitdaBridgeRows(period: CostPeriod = 'month'): EbitdaBridgeRow[] {
  const g = gmMoney(period);
  const raw = [
    {id:'br-0',label:'Budget',value:g.mtd.ebitda.target,bold:true},
    ...g.bridge,
    {id:'br-6',label:'Actual operating EBITDA',value:g.mtd.ebitda.actual,bold:true},
  ];
  const scale = Math.max(1, ...raw.map(r=>Math.abs(r.value)));
  return raw.map(r=>({...r,value:r.value/1000,barPct:Math.min(100,Math.abs(r.value)/scale*100)}));
}

/** One bar of the Budget → actual waterfall, in US$K. A total stands on zero;
 * a step floats from the running total before it to the one after it. */
export type EbitdaWaterfallBar = {
  id: string;
  name: string;
  sub: string;
  kind: 'total' | 'up' | 'down' | 'flat';
  from: number;
  to: number;
  valueLabel: string;
};

const STEP_NAMES: Record<string, { name: string; sub: (label: string) => string }> = {
  volume: { name: 'Volume', sub: (label) => label.split(' · ')[1] ?? 'dst milled' },
  recovery: { name: 'Recovery', sub: () => 'Zn · Pb · Ag' },
  price: { name: 'Price', sub: () => 'same deck' },
  assay: { name: 'Assays', sub: () => 'conc. grade' },
  cost: { name: 'Cost', sub: () => 'non-volume' },
};

const kilo = (value: number) =>
  Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Largest-remainder rounding to hundredths: each value's floor, then one
 * hundredth to the largest remainders until the parts reach `targetHundredths`,
 * so the rounded parts sum exactly to the rounded total. Pure.
 */
export function largestRemainderHundredths(values: readonly number[], targetHundredths: number): number[] {
  const scaled = values.map((value) => value * 100);
  const floors = scaled.map((value) => Math.floor(value + 1e-9));
  let deficit = targetHundredths - floors.reduce((sum, value) => sum + value, 0);
  const order = scaled
    .map((value, index) => ({ index, remainder: value - floors[index]! }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  const out = [...floors];
  for (let i = 0; deficit !== 0 && order.length > 0; i += 1) {
    const { index } = order[i % order.length]!;
    out[index]! += deficit > 0 ? 1 : -1;
    deficit += deficit > 0 ? -1 : 1;
  }
  return out;
}

/** The bridge drawn as a waterfall: the same `ebitdaBridgeRows()` values, with
 * each step's running total, so the bars sum from Budget to actual on the glass.
 * The step labels are rounded by largest remainder (K12e round 2), so Budget
 * plus the displayed steps equals the displayed Actual to the US$10. */
export function ebitdaWaterfall(period: CostPeriod = 'month'): { bars: EbitdaWaterfallBar[]; max: number } {
  const rows = ebitdaBridgeRows(period);
  const totals = rows.filter((row) => row.bold);
  const steps = rows.filter((row) => !row.bold);
  const budgetH = Math.round((totals[0]?.value ?? 0) * 100);
  const actualH = Math.round((totals[totals.length - 1]?.value ?? 0) * 100);
  const stepH = largestRemainderHundredths(
    steps.map((row) => row.value),
    actualH - budgetH,
  );
  const displayed = new Map(steps.map((row, index) => [row.id, stepH[index]!]));
  const bars: EbitdaWaterfallBar[] = [];
  let running = 0;
  for (const row of rows) {
    if (row.bold) {
      bars.push({
        id: row.id,
        name: row.id === 'br-0' ? 'Budget' : 'Actual',
        sub: 'EBITDA',
        kind: 'total',
        from: 0,
        to: row.value,
        valueLabel: kilo(row.value),
      });
      running = row.value;
      continue;
    }
    const names = STEP_NAMES[row.id] ?? { name: row.label, sub: () => '' };
    const next = running + row.value;
    const rounded = displayed.get(row.id) ?? Math.round(row.value * 100);
    bars.push({
      id: row.id,
      name: names.name,
      sub: names.sub(row.label),
      kind: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat',
      from: running,
      to: next,
      valueLabel: `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${kilo(rounded / 100)}`,
    });
    running = next;
  }
  const max = Math.max(...bars.map((bar) => Math.max(bar.from, bar.to)));
  return { bars, max };
}
